import { useCallback, useContext, useMemo } from 'react';
import { teamContext } from './team.context';
import {
  ROUTE_PERMISSIONS,
  type RoutePermissionKey,
  SETTINGS_PERMISSIONS,
  type SettingsPermissionKey,
} from './team.permissions.map';
import {
  deniesGlobalAdmin,
  hasEveryTeamPermission,
  hasSomeTeamPermission,
  type LegacyGate,
  resolveTeamPermission,
  resolveTeamPermissionSet,
  type TeamPermission,
} from './team.permissions.utils';

const NO_PERMISSIONS: readonly TeamPermission[] = [];

function useTeamPermissionState() {
  const tenant = useContext(teamContext);
  const permissionBundles = tenant.membership?.permissionBundles;

  return {
    permissions: tenant.membership?.permissions ?? NO_PERMISSIONS,
    isUiPermissionRolesEnabled: tenant.usesUiPermissionRoles,
    // The legacy flags stay sourced from the session context rather than
    // `useTeamRole`, which resolves `isTeamAdmin` from a bundle name once the
    // feature is enabled. Only the pre-feature role matters here.
    isTeamAdmin: tenant.isTeamAdmin,
    isTeamUser: tenant.membership?.role === 'user',
    isVrpAdmin: tenant.isVrpAdmin,
    permissionBundles,
  };
}

/**
 * Gate a navigation item, route, or action on one effective primitive
 * permission for the selected team.
 *
 * Pass `legacy` to describe what the replaced code did while
 * the team still uses legacy roles (VRI client teams). The default `allow` suits a gate
 * that has no pre-feature equivalent, so a new check cannot change behavior
 * for an unmigrated team.
 *
 * ```tsx
 * // the members page was visible to every member before the feature
 * const canReadMembers = useTeamPermission('team-members:read');
 *
 * // the security page was admin-only before the feature
 * const canReadSecurity = useTeamPermission('team-settings:read', {
 *   legacy: 'teamAdmin',
 * });
 * ```
 */
export function useTeamPermission(
  permission: TeamPermission,
  options: { legacy?: LegacyGate } = {}
): boolean {
  const { legacy = 'allow' } = options;
  const state = useTeamPermissionState();

  return useMemo(
    () =>
      resolveTeamPermission({
        permission,
        permissions: state.permissions,
        isUiPermissionRolesEnabled: state.isUiPermissionRolesEnabled,
        legacy,
        isTeamAdmin: state.isTeamAdmin,
        isTeamUser: state.isTeamUser,
        isVrpAdmin: state.isVrpAdmin,
      }),
    [
      permission,
      legacy,
      state.permissions,
      state.isUiPermissionRolesEnabled,
      state.isTeamAdmin,
      state.isTeamUser,
      state.isVrpAdmin,
    ]
  );
}

/**
 * Check several permissions without stacking `useTeamPermission` calls, for
 * components that gate more than one surface. `has` follows the same rules as
 * `useTeamPermission`, including the legacy fallback.
 *
 * `permissions` is the raw effective array and is empty while the feature is
 * disabled. Use `has`/`hasAny`/`hasAll` for gating; read `permissions`
 * directly only to display or debug the resolved access.
 */
export function useTeamPermissions() {
  const state = useTeamPermissionState();

  const has = useCallback(
    (permission: TeamPermission, options: { legacy?: LegacyGate } = {}) =>
      resolveTeamPermission({
        permission,
        permissions: state.permissions,
        isUiPermissionRolesEnabled: state.isUiPermissionRolesEnabled,
        legacy: options.legacy ?? 'allow',
        isTeamAdmin: state.isTeamAdmin,
        isTeamUser: state.isTeamUser,
        isVrpAdmin: state.isVrpAdmin,
      }),
    [
      state.permissions,
      state.isUiPermissionRolesEnabled,
      state.isTeamAdmin,
      state.isTeamUser,
      state.isVrpAdmin,
    ]
  );

  const hasAll = useCallback(
    (permissions: readonly TeamPermission[]) =>
      state.isUiPermissionRolesEnabled
        ? hasEveryTeamPermission(state.permissions, permissions)
        : !deniesGlobalAdmin(permissions, state.isVrpAdmin),
    [state.permissions, state.isUiPermissionRolesEnabled, state.isVrpAdmin]
  );

  const hasAny = useCallback(
    (permissions: readonly TeamPermission[]) => {
      return state.isUiPermissionRolesEnabled
        ? hasSomeTeamPermission(state.permissions, permissions)
        : !state.isVrpAdmin ||
            permissions.some(
              (permission) => !deniesGlobalAdmin([permission], true)
            );
    },
    [state.permissions, state.isUiPermissionRolesEnabled, state.isVrpAdmin]
  );

  return useMemo(
    () => ({
      has,
      hasAll,
      hasAny,
      permissions: state.permissions,
      permissionBundles: state.permissionBundles ?? [],
      isUiPermissionRolesEnabled: state.isUiPermissionRolesEnabled,
    }),
    [
      has,
      hasAll,
      hasAny,
      state.permissions,
      state.permissionBundles,
      state.isUiPermissionRolesEnabled,
    ]
  );
}

/**
 * Gate a top-level product area. Navigation items and route elements call this
 * with the same key so a visible link always reaches a rendered route.
 */
export function useRoutePermission(key: RoutePermissionKey): boolean {
  const { permissions, legacy } = ROUTE_PERMISSIONS[key];

  return useMappedPermission(permissions, legacy);
}

/** Gate a settings page. See `useRoutePermission` for the shared-key rule. */
export function useSettingsPermission(key: SettingsPermissionKey): boolean {
  const { permissions, legacy } = SETTINGS_PERMISSIONS[key];

  return useMappedPermission(permissions, legacy);
}

function useMappedPermission(
  required: readonly TeamPermission[],
  legacy: LegacyGate
): boolean {
  const state = useTeamPermissionState();

  return useMemo(
    () =>
      resolveTeamPermissionSet({
        required,
        permissions: state.permissions,
        isUiPermissionRolesEnabled: state.isUiPermissionRolesEnabled,
        legacy,
        isTeamAdmin: state.isTeamAdmin,
        isTeamUser: state.isTeamUser,
        isVrpAdmin: state.isVrpAdmin,
      }),
    [
      required,
      legacy,
      state.permissions,
      state.isUiPermissionRolesEnabled,
      state.isTeamAdmin,
      state.isTeamUser,
      state.isVrpAdmin,
    ]
  );
}
